/**
 * Categories Hooks
 *
 * React Query hooks for Raw Product Categories data fetching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  categoriesService,
  type CreateCategoryDto,
  type UpdateCategoryDto,
  type MoveCategoryDto,
} from '../services/raw-products.service';

/**
 * Hook to fetch categories tree
 */
export function useCategories() {
  return useQuery({
    queryKey: ['raw-product-categories', 'tree'],
    queryFn: () => categoriesService.getTree(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch categories as flat list (for selects)
 */
export function useFlatCategories() {
  return useQuery({
    queryKey: ['raw-product-categories', 'flat'],
    queryFn: () => categoriesService.getFlat(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch single category by ID
 */
export function useCategory(id: string | null) {
  return useQuery({
    queryKey: ['raw-product-categories', id],
    queryFn: () => categoriesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a category
 */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCategoryDto) => categoriesService.create(data),
    onSuccess: (category) => {
      queryClient.invalidateQueries({ queryKey: ['raw-product-categories'] });
      toast.success(`Categorie "${category.name}" creee avec succes`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la creation de la categorie';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a category
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryDto }) =>
      categoriesService.update(id, data),
    onSuccess: (category) => {
      queryClient.invalidateQueries({ queryKey: ['raw-product-categories'] });
      toast.success(`Categorie "${category.name}" mise a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour';
      toast.error(message);
    },
  });
}

/**
 * Hook to move a category
 */
export function useMoveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveCategoryDto }) =>
      categoriesService.move(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-product-categories'] });
      toast.success('Categorie deplacee avec succes');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors du deplacement';
      toast.error(message);
    },
  });
}

/**
 * Hook to archive a category
 */
export function useArchiveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categoriesService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-product-categories'] });
      toast.success('Categorie archivee avec succes');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de l'archivage";
      toast.error(message);
    },
  });
}
